import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WhatsAppGlobalConfig {
  id: string;
  evolution_api_url: string;
  evolution_api_key: string;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const useWhatsAppGlobalConfig = () => {
  const [config, setConfig] = useState<WhatsAppGlobalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('whatsapp_global_config')
        .select('*')
        .eq('is_active', true)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      setConfig(data || null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar configuração';
      setError(errorMessage);
      console.error('Error loading WhatsApp global config:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (apiUrl: string, apiKey: string) => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Desativar config anterior se existir
      if (config) {
        await supabase
          .from('whatsapp_global_config')
          .update({ is_active: false })
          .eq('id', config.id);
      }

      // Criar nova config
      const { data, error: insertError } = await supabase
        .from('whatsapp_global_config')
        .insert({
          evolution_api_url: apiUrl,
          evolution_api_key: apiKey,
          is_active: true,
          created_by: user.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setConfig(data);
      toast.success('Configuração global salva com sucesso!');
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar configuração';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (_apiUrl: string, _apiKey: string) => {
    // Evolution foi descontinuado neste projeto (migração para WPPConnect/Railway).
    toast.error('Teste de conexão Evolution desativado (use WPPConnect).');
    return false;
  };

  useEffect(() => {
    loadConfig();
  }, []);

  return {
    config,
    loading,
    error,
    loadConfig,
    saveConfig,
    testConnection,
  };
};
